import { makeStyles, withStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';

const useStyles = makeStyles((theme) => ({
  noChallenges: {
    display: 'table-cell',
    textAlign: 'center',
    padding: '50px',
    '& svg': {
      width: '40px',
      height: '40px',
      color: theme.palette.primary.main,
    },
    '& h4': {
      color: theme.palette.primary.main,
      fontWeight: '200',
    },
  },
  projectName: {
    paddingLeft: '16px',
  },
  icon: {
    '& svg': {
      color: theme.palette.custom.iconAccent,
    },
    '& svg:hover': {
      color: theme.palette.custom.iconAccent,
    },
  },
  header: {
    display: 'flex',
    paddingBottom: '20px',
  },
  title: {
    flexGrow: 1,
    letterSpacing: '-0.5px',
  },
  underlined: {
    textDecoration: 'underline',
  },
  searchToolbar: {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '13px',
      width: '100vh',
      flexGrow: 1,
    },
    '& .Mui-focused': {
      border: `1px solid ${theme.palette.secondary.gray}`,
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.secondary.gray,
    },
    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: '2px',
    },
    '& button': {
      backgroundColor: theme.palette.primary.white,
      color: theme.palette.secondary.gray,
      fontSize: '13.5px',
      '& svg': {
        color: theme.palette.secondary.gray,
      },
    },
    '& .MuiMenuItem-root': {
      color: theme.palette.custom.menuItemText,
    },
  },
  boxTitle: {
    flexGrow: 1,
  },
  optionsContainer: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  formWrapper: {
    width: '70vw',
    paddingBottom: '10px',
    '& .MuiFormLabel-root': {
      color: theme.palette.custom.inputLabel,
      margin: '15px auto 8px',
      fontSize: '12px',
    },
    '& input': {
      backgroundColor: theme.palette.custom.inputBackground,
      padding: '10px',
    },
    '& .MuiSelect-root': {
      padding: '10px',
      fontSize: '13.5px',
    },
  },
  challengeTable: {
    '& .MuiTableCell-root': {
      color: theme.palette.primary.gray,
    },
    '& .MuiTableCell-head': {
      color: theme.palette.primary.gray,
    },
    '& .MuiTableCell-paddingNone': {
      color: theme.palette.primary.mainBlue,
      fontWeight: 'bold',
    },
    '& .Mui-checked': {
      color: theme.palette.secondary.blue,
    },
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.custom.tableRowHover,
      },
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.custom.tableRowHover,
    },
  },
  tableHeader: {
    '& .Mui-checked': {
      color: theme.palette.secondary.blue,
      opacity: 0.5,
    },
    '& .MuiCheckbox-indeterminate ': {
      color: theme.palette.secondary.blue,
      opacity: 0.5,
    },
  },
  progress: {
    display: 'table-cell',
    textAlign: 'center',
    padding: '50px',
    '& div': {
      color: theme.palette.primary.main,
    },
    '& h4': {
      color: theme.palette.primary.main,
      fontWeight: '200',
    },
  },
  tag: {
    borderRadius: '5px',
    padding: '5px 8px',
    maxWidth: '160px',
  },
  // Same light values the `secondary.*` pairs had; those keys are inherited
  // verbatim in dark mode, which left pastel fills glowing on a dark surface.
  // Careful: the pairings here are NOT the same as in the QA / Teleaudit chips
  // (here "waiting for approval" is orange and "close" is blue), so each one is
  // mapped by its actual colour rather than by its name.
  // Applied in index.js: tagWaitinfForApproval (Pendiente), tagProcessing
  // (Explicado), tagClose (Resuelto), tagNA (default). The other four are
  // defined but never used — migrated so re-wiring them stays safe.
  tagOpen: {
    color: theme.palette.custom.statusOpenText,
    backgroundColor: theme.palette.custom.statusOpenBackground,
  },
  tagWaitinfForApproval: {
    color: theme.palette.custom.statusPendingText,
    backgroundColor: theme.palette.custom.statusPendingBackground,
  },
  tagApproved: {
    color: theme.palette.custom.statusApprovedText,
    backgroundColor: theme.palette.custom.statusApprovedBackground,
  },
  tagProcessing: {
    color: theme.palette.custom.statusNeutralText,
    backgroundColor: theme.palette.custom.statusNeutralBackground,
  },
  tagClose: {
    color: theme.palette.custom.statusClosedText,
    backgroundColor: theme.palette.custom.statusClosedBackground,
  },
  tagRejected: {
    color: theme.palette.custom.statusRejectedText,
    backgroundColor: theme.palette.custom.statusRejectedBackground,
  },
  tagPendingDate: {
    color: theme.palette.custom.statusIdleText,
    backgroundColor: theme.palette.custom.statusIdleBackground,
  },
  tagNA: {
    color: theme.palette.custom.statusIdleText,
    backgroundColor: theme.palette.custom.statusIdleBackground,
  },
  root: {
    width: '60vh',
    maxWidth: 500,
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  avatar: {
    backgroundColor: theme.palette.primary.blue,
  },
  success: {
    color: theme.palette.primary.success,
    borderColor: theme.palette.primary.success,
    borderWidth: '2px',
    borderRadius: '6px',
  },
  error: {
    color: theme.palette.primary.error,
    borderColor: theme.palette.primary.error,
    borderWidth: '2px',
    borderRadius: '6px',
  },
  warning: {
    color: theme.palette.primary.warning,
    borderColor: theme.palette.primary.warning,
    borderWidth: '2px',
    borderRadius: '6px',
    backgroundColor: theme.palette.transparent.warning,
  },
  processing: {
    color: theme.palette.primary.blue,
    borderColor: theme.palette.primary.blue,
    borderWidth: '2px',
    borderRadius: '6px',
  },
  align: {
    textAlign: 'center',
    paddingTop: '15px',
  },
  input: {
    display: 'none',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& > * + *': {
      marginLeft: theme.spacing(2),
    },
    '& svg': {
      color: theme.palette.custom.dangerIcon,
      margin: '0px',
    },
    '& svg:hover': {
      opacity: '0.8',
    },
  },
  uploadIcon: {
    '& svg': {
      height: '50px',
      width: '100%',
      color: theme.palette.custom.iconAccent,
      margin: '0px',
    },
    '& svg:hover': {
      opacity: '0.8',
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  labelDates: {
    margin: '0 0 0 8px !important',
  },
  formControlDates: {
    minWidth: 200,
    margin: '8px',
  },
}));

// NOTE: these three exports are not imported anywhere in this module — the only
// consumer of these names lives in QA. They are kept in sync with the copies in
// Teleaudit/questionResponsesView/styles.js so the trio does not drift.
export const RedBorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.custom.progressBarDanger,
  },
}))(LinearProgress);

export const YellowBorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.custom.progressBarWarning,
  },
}))(LinearProgress);

export const GreenBorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.custom.progressBarSuccess,
  },
}))(LinearProgress);

export default useStyles;
